import { useApolloClient } from '@apollo/client';
import { Form, message } from 'antd';
import { Formik } from 'formik';
import { pick } from 'lodash/fp';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { PromoCodeModuleSpecsFragment } from '../../../../api/fragments/PromoCodeModuleSpecs';
import {
    type UpdatePromoCodeModuleMutation,
    type UpdatePromoCodeModuleMutationVariables,
    UpdatePromoCodeModuleDocument,
} from '../../../../api/mutations/updatePromoCodeModule';
import type { PromoCodeModuleSettings } from '../../../../api/types';
import InputField from '../../../../components/fields/InputField';
import useHandleError from '../../../../utilities/useHandleError';
import useValidator from '../../../../utilities/useValidator';
import validators from '../../../../utilities/validators';

export type PromoCodeModuleUpdateFormProps = {
    module: PromoCodeModuleSpecsFragment;
    onComplete: () => void;
};

type FormValues = PromoCodeModuleSettings;

const PromoCodeModuleUpdateForm = ({ module, onComplete }: PromoCodeModuleUpdateFormProps) => {
    const { t } = useTranslation(['promoCodeModuleDetails']);
    const apolloClient = useApolloClient();

    const moduleId = module.id;

    const onSubmit = useHandleError(
        async (values: FormValues) => {
            message.loading({
                content: t('promoCodeModuleDetails:updateDrawer.messages.updateSubmitting'),
                key: 'primary',
                duration: 0,
            });

            await apolloClient
                .mutate<UpdatePromoCodeModuleMutation, UpdatePromoCodeModuleMutationVariables>({
                    mutation: UpdatePromoCodeModuleDocument,
                    variables: { moduleId, settings: values },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            message.success({
                content: t('promoCodeModuleDetails:updateDrawer.messages.updateSuccess'),
                key: 'primary',
            });

            onComplete();
        },
        [onComplete, apolloClient, moduleId, t]
    );

    const initialValues = useMemo(
        (): FormValues => ({
            ...pick(['displayName'], module),
        }),
        [module]
    );

    const validate = useValidator(validators.requiredNonEmptyString('displayName'));

    return (
        <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
            {({ handleSubmit }) => (
                <Form
                    id="updatePromoCodeModule"
                    layout="vertical"
                    name="updatePromoCodeModule"
                    onSubmitCapture={handleSubmit}
                >
                    <InputField
                        {...t('promoCodeModuleDetails:updateDrawer.fields.displayName', {
                            returnObjects: true,
                        })}
                        name="displayName"
                        required
                    />
                </Form>
            )}
        </Formik>
    );
};

export default PromoCodeModuleUpdateForm;
